import config from './config';

// Should load Facebook SDK once either on index or first permanent component
// using initFacebookSdk
// then call launchWhatsAppSignup() later wards

type FacebookInitParams = {
  appId: string;
  cookie: boolean;
  xfbml: boolean;
  version: string;
};

type LoginResponse = { authResponse?: { accessToken?: string; code?: string } };
type BusinessCategory =
  | 'AUTO'
  | 'BEAUTY'
  | 'APPAREL'
  | 'EDU'
  | 'ENTERTAIN'
  | 'EVENT_PLAN'
  | 'FINANCE'
  | 'GROCERY'
  | 'GOVT'
  | 'HOTEL'
  | 'NONPROFIT'
  | 'PROF_SERVICES'
  | 'RETAIL'
  | 'TRAVEL'
  | 'RESTAURANT'
  | 'OTHER';
type SetupData = {
  business?: {
    name?: string;
    email?: string;
    website?: string;
    phone?: {
      code?: number;
      number?: string;
    };
    address?: {
      streetAddress1?: string;
      streetAddress2?: string;
      city?: string;
      state?: string;
      zipPostal?: string;
      country?: string;
    };
    timezone?: string;
  };
  phone?: {
    displayName?: string;
    category?: BusinessCategory;
    description?: string;
  };
};
// Embedded Signup v4: the version comes from the Facebook Login for Business
// configuration (`config_id`), so `extras` carries no version fields.
type LoginParams = {
  scope?: string;
  config_id?: string;
  response_type?: string;
  override_default_response_type?: boolean;
  auth_type?: 'rerequest' | 'reauthenticate' | 'reauthorize';
  extras?: {
    setup?: SetupData;
  };
};
type LoginCallback = (response: LoginResponse) => void;
type FBLogin = (callback: LoginCallback, loginParams: LoginParams) => void;

type ApiResponse = {
  error: string;
  data: {
    granular_scopes: [{ scope: string; target_ids: string[] }];
  };
};
type ApiCallback = (response: ApiResponse) => void;
type FBApi = (callbackUrl: string, callback: ApiCallback) => void;

declare global {
  interface Window {
    fbAsyncInit(): void;
    FB: {
      init(params: FacebookInitParams): void;
      login: FBLogin;
      api: FBApi;
    };
    appConfig: {
      AUTH0_DOMAIN: string;
      AUTH0_CLIENT_ID: string;
      AUTH0_RESPONSE_TYPE: string;
      REDIRECT_URI: string;
      SERVER_BASE_URL: string;
      FACEBOOK_APP_ID: string;
      EMBEDDED_SIGNUP_CONFIGURE_ID: string;
      KEYREPLY_BUSINESS_ID: string;
      COHERE_API_KEY: string;
    };
    fbq(trackName: string, trackType: string, options: { appId: string; feature: string }): void;
  }
}

export function initFacebookSdk() {
  // wait for facebook sdk to initialize before starting the react app
  window.fbAsyncInit = function () {
    console.log('FB');
    window.FB.init({
      appId: config.FACEBOOK_APP_ID,
      cookie: true,
      xfbml: true,
      version: 'v21.0',
    });
  };

  type FacebookElement = { src: string } & HTMLElement;
  // load facebook sdk script
  (function (d, s, id) {
    let js = <FacebookElement>d.getElementsByTagName(s)[0];
    const fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) {
      return;
    }
    js = <FacebookElement>d.createElement(s);
    js.id = id;
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    fjs?.parentNode?.insertBefore(js, fjs);
  })(document, 'script', 'facebook-jssdk');
}

function waitForFbq(callback: () => void) {
  if (typeof window.fbq !== 'undefined') {
    callback();
  } else {
    setTimeout(function () {
      waitForFbq(callback);
    }, 100);
  }
}

// Rejection for a popup that completed without a code: a configuration problem
// (response_type or configuration ID), not a user cancelling. Callers compare
// against it to avoid recording it as a cancellation.
export const NO_AUTH_CODE_MESSAGE =
  'Facebook did not return an authorization code. Check the Embedded Signup configuration.';

// Resolves with the Embedded Signup code. The server exchanges it for the
// customer's token (the app secret stays server side), and the code expires 30
// seconds after it is issued, so send it to `/wabas/connect` straight away.
export function launchWhatsAppSignup(): Promise<string> {
  return new Promise((resolve, reject) => {
    waitForFbq(function () {
      window.fbq &&
        window.fbq('trackCustom', 'WhatsAppOnboardingStart', {
          appId: config.FACEBOOK_APP_ID,
          feature: 'whatsapp_embedded_signup',
        });
    });
    window.FB.login(
      function (response: LoginResponse) {
        if (!response.authResponse) {
          reject('User cancelled login or did not fully authorize.');
          return;
        }
        const code = response.authResponse.code;
        if (code) {
          resolve(code);
        } else {
          // The popup completed but returned no code: a configuration problem
          // (response_type), not a user cancelling.
          reject(NO_AUTH_CODE_MESSAGE);
        }
      },
      {
        config_id: config.EMBEDDED_SIGNUP_CONFIGURE_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
        },
      },
    );
  });
}

// export function mockLaunchWhatsAppSignup(): Promise<string> {
//   return new Promise((resolve, reject) => {
//     const newWindow = window.open('mockSignup', 'name', 'height=400,width=300');
//     newWindow!.onbeforeunload = function () {
//       resolve('TOKEN');
//     };
//   });
//}
